import { CourseContent, MartechApiClient } from "@ilc-technology/martech-api-client";
import { AppConfig } from "../../AppConfig";
import { Segment, SegmentType } from "../Types";

export type { CourseContent };

export type CourseContentByDestination = {
  destinationCode: string;
  courses: Array<CourseContent>;
};

let martechApiClient: MartechApiClient | undefined;

function getMartechApiClient(): MartechApiClient {
  if (!martechApiClient) {
    const isProduction = AppConfig.environment?.toLowerCase() === "prod";
    martechApiClient = new MartechApiClient({ isProduction });
  }

  return martechApiClient;
}

const normalizeCourseCode = (courseCode: string) => courseCode.toUpperCase();

function isCourseNotFoundError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes("course-content-not-found") ||
    message.includes("courses-models-not-found") ||
    message.includes("404")
  );
}

async function fetchCourseContent(
  market: string,
  product: string,
  courseCode: string
): Promise<CourseContent | null> {
  try {
    return await getMartechApiClient().getCourseContent(market, product, courseCode);
  } catch (error) {
    if (isCourseNotFoundError(error)) {
      return null;
    }

    throw error;
  }
}

export async function getCourseContentForQuote(
  market: string,
  product: string,
  segments: Array<Segment>,
  additionalCourseCodes: string[] = []
): Promise<CourseContentByDestination[]> {
  const courseSegments = segments.filter((segment) => segment.type === SegmentType.Course);
  const destinationCodes = [...new Set(courseSegments.map((segment) => segment.destinationCode))];

  const courseCodes = [
    ...new Set([
      ...courseSegments.map((segment) => segment.course.code),
      ...additionalCourseCodes,
    ]),
  ];

  const courses = (
    await Promise.all(courseCodes.map((courseCode) => fetchCourseContent(market, product, courseCode)))
  ).filter((course): course is CourseContent => course !== null);

  const coursesByCode = new Map(courses.map((course) => [normalizeCourseCode(course.courseCode), course]));

  const normalizedAdditionalCourseCodes = additionalCourseCodes.map(normalizeCourseCode);

  return destinationCodes.map((destinationCode) => {
    const segmentCourseCodes = [
      ...new Set(
        courseSegments
          .filter((segment) => segment.destinationCode === destinationCode)
          .map((segment) => normalizeCourseCode(segment.course.code))
      ),
    ];

    const destinationCourseCodes = [...new Set([...segmentCourseCodes, ...normalizedAdditionalCourseCodes])];

    return {
      destinationCode,
      courses: destinationCourseCodes
        .map((courseCode) => coursesByCode.get(courseCode))
        .filter((course): course is CourseContent => course !== undefined),
    };
  });
}
